import { Button, Typography } from '@breetometer/components';
import { IdentityVerification, RecoveryCodesExhaustedIcon } from 'BreetAssets';
import { DialogAccessCredentials, DialogLoading, Loader, ModalParts, RecoveryCodeForm } from 'BreetComponents';
import {
	alwaysRefetchApiQueryOptions,
	handleApiError,
	type IUseAccessCredentialsRequest,
	openIntercom,
	OTPLENGTH,
	showToast,
	type TwoFaType,
} from 'BreetHelpers';
import { useAccessCredentials, useFormStage, useModalApi } from 'BreetHooks';
import {
	selectUser,
	useAppSelector,
	useConvertFiatToUSDMutation,
	useGetUserPreferenceQuery,
	useRequestPaymentMutation,
	useSwapCurrencyMutation,
	useWithdrawUSDMutation,
} from 'BreetRedux';
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
	type IDIALOGVERIFYTRANSACTIONPROPS,
	type VERIFYTRANSACTIONSTAGETYPE,
	verifyTransactionTitle,
} from './DialogVerifyTransaction.helpers';

const DialogVerifyTransaction = () => {
	const userData = useAppSelector(selectUser);

	const [requestPayment, { isLoading: requestPaymentLoading }] = useRequestPaymentMutation();

	const [withdrawUSD, { isLoading: withdrawUSDLoading }] = useWithdrawUSDMutation();

	const [convertFiatToUSD, { isLoading: convertFiatToUSDLoading }] = useConvertFiatToUSDMutation();

	const [swapCurrency, { isLoading: swapCurrencyLoading }] = useSwapCurrencyMutation();

	const isApiloading = requestPaymentLoading || withdrawUSDLoading || convertFiatToUSDLoading || swapCurrencyLoading;

	const [pin, setPin] = useState<string>('');

	const [recoveryCodeValue, setRecoveryCodeValue] = useState<string>('');

	const formStage = useFormStage<VERIFYTRANSACTIONSTAGETYPE>(
		'pin-verification',
		['pin-verification', 'otp-verification', 'recovery-code', 'exhausted-recovery-code'],
		verifyTransactionTitle
	);

	const { getModalProps, closeModalDirect, handleModalCloseAll } = useModalApi();

	const modalProps = getModalProps('verifyTransaction');

	const { transactionType, withdrawFiatReqData, withdrawUSDReqData, convertFiatToUSDReqData, swapCurrencyReqData } =
		modalProps?.metadata as IDIALOGVERIFYTRANSACTIONPROPS;

	const skipPinAnd2fa = useMemo(() => transactionType === 'convertFiatToUSD', [transactionType]);

	const { data: userPreferenceData, isLoading: userPreferenceLoading } = useGetUserPreferenceQuery(
		undefined,
		alwaysRefetchApiQueryOptions
	);

	const is2fa = useMemo(
		() => !!userPreferenceData?.data?.isAuthenticator2FaActive || !!userPreferenceData?.data?.is2FAActive,
		[userPreferenceData]
	);

	const skip2fa = useMemo(() => transactionType === 'swapCurrency' || !is2fa, [is2fa, transactionType]);

	const fetchOTP = useCallback(
		(pinArg: string, otpArg?: TwoFaType) => {
			const withOtp = <T extends object>(body: T): T & { otp?: TwoFaType } => (otpArg ? { ...body, otp: otpArg } : body);

			let apiRequest: Promise<unknown> | undefined;

			switch (transactionType) {
				case 'withdraw-fiat': {
					if (withdrawFiatReqData) {
						const withdrawReq = { ...withdrawFiatReqData, pin: pinArg };
						apiRequest = requestPayment(withOtp(withdrawReq)).unwrap();
					}
					break;
				}
				case 'withdrawUSD': {
					if (withdrawUSDReqData) {
						const withdrawUSDReq = { ...withdrawUSDReqData, pin: pinArg };
						apiRequest = withdrawUSD(withOtp(withdrawUSDReq)).unwrap();
					}
					break;
				}
				case 'convertFiatToUSD': {
					if (convertFiatToUSDReqData) {
						const convertFiatUSDReq = { ...convertFiatToUSDReqData, pin: pinArg };
						apiRequest = convertFiatToUSD(withOtp(convertFiatUSDReq)).unwrap();
					}
					break;
				}
				case 'swapCurrency': {
					if (swapCurrencyReqData) {
						const swapCurrencyReq = { ...swapCurrencyReqData, pin: pinArg };
						apiRequest = swapCurrency(withOtp(swapCurrencyReq)).unwrap();
					}
					break;
				}
				default:
					break;
			}

			if (!apiRequest) {
				showToast('error', 'Something went wrong');
				return;
			}

			return apiRequest;
		},
		[
			convertFiatToUSD,
			convertFiatToUSDReqData,
			requestPayment,
			swapCurrency,
			swapCurrencyReqData,
			transactionType,
			withdrawFiatReqData,
			withdrawUSD,
			withdrawUSDReqData,
		]
	);

	const submitRequest = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			if (!data?.code) {
				showToast('error', 'Something went wrong');
				return;
			}

			const defaultType = userPreferenceData?.data?.is2FAActive ? 'email' : 'authenticator';

			const otpProps: TwoFaType = {
				code: data.code,
				type: formStage.isStageActive('recovery-code') ? 'recoveryCode' : defaultType,
			};

			fetchOTP(pin, otpProps)
				?.then(() => {
					setPin('');
					data.onSuccess?.();
					handleModalCloseAll();
				})
				.catch((e: unknown) => {
					data.onError(e);
				});
		},
		[fetchOTP, formStage, handleModalCloseAll, pin, userPreferenceData?.data?.is2FAActive]
	);

	const refectchOTP = useCallback(async () => {
		await fetchOTP(pin)?.catch(handleApiError);
	}, [fetchOTP, pin]);

	const description = userPreferenceData?.data?.is2FAActive
		? `Enter the ${OTPLENGTH}-digit OTP sent to your email to confirm withdrawal`
		: `Enter the ${OTPLENGTH}-digit code from your Authenticator app to continue`;

	const handleRecoveryCodeValue = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setRecoveryCodeValue(e.target.value);
	}, []);

	const submitRecoveryCode = useCallback(() => {
		if (userPreferenceData?.data?.usedRecoveryCodesCount === 10) {
			formStage.navigator.goTo('exhausted-recovery-code')();
			return;
		}

		submitRequest({
			code: recoveryCodeValue,
			onError: () => {
				setRecoveryCodeValue('');
			},
		});
	}, [recoveryCodeValue, submitRequest, userPreferenceData?.data?.usedRecoveryCodesCount, formStage]);

	const { component: otpComponent } = useAccessCredentials({
		id: '2fa-step',
		mode: 'otp',
		submitRequest,
		dialogMessage: 'Verifying OTP...',
		title: 'Verify Your Identity',
		description,
		type: 'collect',
		persistOnVerify: true,
		generateOTP: false,
		resendOtp: userPreferenceData?.data?.is2FAActive,
		render: (props) => (
			<DialogAccessCredentials
				{...props}
				refetchFn={refectchOTP}
				otpCta={
					userPreferenceData?.data?.is2FAActive
						? undefined
						: { btnTitle: 'Use Backup Code', message: "Can't find code?", action: formStage.navigator.goTo('recovery-code') }
				}
			/>
		),
	});

	const requestEmailOTP = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			if (!data?.code) {
				showToast('error', 'Something went wrong');
				return;
			}

			fetchOTP(data.code)
				?.then(() => {
					if (skip2fa) {
						data.onSuccess?.();
						handleModalCloseAll();
					}
				})
				.catch((e: unknown) => {
					closeModalDirect('loadingDialog');

					const error = e as { data: { message: string } };

					if (error.data.message === 'please enter email otp' && data.code) {
						setPin(data.code);

						formStage.navigator.goTo('otp-verification')();
					} else {
						data.onError(e);
					}
				});
		},
		[closeModalDirect, fetchOTP, formStage.navigator, handleModalCloseAll, skip2fa]
	);

	const requestAuthenticatorOtp = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			closeModalDirect('loadingDialog');

			if (!data?.code) {
				showToast('error', 'Something went wrong');
				return;
			}

			setPin(data.code);

			formStage.navigator.goTo('otp-verification')();
		},
		[closeModalDirect, formStage.navigator]
	);

	const { component: pinComponent } = useAccessCredentials({
		id: 'withdraw-request',
		mode: 'pin',
		// if authenticator is active use requestAuthenticatorOtp
		// if authenticatoe isnt active not 2fa isnt active, use requestEmailOTP
		submitRequest: userPreferenceData?.data?.isAuthenticator2FaActive ? requestAuthenticatorOtp : requestEmailOTP,
		dialogMessage: 'Processing request...',
		type: 'collect',
		render: (props) => <DialogAccessCredentials {...props} />,
	});

	const callOnceRef = useRef<boolean>(false);

	useEffect(() => {
		if (skipPinAnd2fa && !callOnceRef.current) {
			callOnceRef.current = true;

			const element = document.getElementById('loading-dialog-message');

			if (element) {
				element.textContent = 'Processing request...';
			}

			fetchOTP('')
				?.then(() => {
					handleModalCloseAll();
				})
				.catch(handleApiError);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const intercomMsg = `Hi Breet, I've used up all my backup codes for my account. \n\nHere is my User ID: \n\n${userData.data?.data?._id}`;

	if (userPreferenceLoading) return <Loader />;

	if (skipPinAnd2fa) return <DialogLoading />;

	return (
		<ModalParts.Wrapper
			title={formStage.stageTitle}
			rightSheet
			root={verifyTransactionTitle['pin-verification']}
			handleBackLink={formStage.navigator.historyBack}
		>
			<ModalParts.Stage hide={!formStage.isStageActive('pin-verification')}>{pinComponent}</ModalParts.Stage>
			<ModalParts.Stage hide={!formStage.isStageActive('otp-verification')}>
				<div className='verify-transaction-otp'>
					<IdentityVerification />
					{otpComponent}
				</div>
			</ModalParts.Stage>
			<ModalParts.Stage hide={!formStage.isStageActive('recovery-code')}>
				<RecoveryCodeForm
					handleRecoveryCodeValue={handleRecoveryCodeValue}
					btnDisabled={recoveryCodeValue.length < 10}
					recoveryCodeValue={recoveryCodeValue}
					submitRecoveryCode={submitRecoveryCode}
					disableRecoveryCode={formStage.navigator.goTo('otp-verification')}
					isLoading={isApiloading}
				/>
			</ModalParts.Stage>
			<ModalParts.Stage hide={!formStage.isStageActive('exhausted-recovery-code')}>
				<div className='exhausted-recovery-code'>
					<RecoveryCodesExhaustedIcon />

					<Typography
						as='h5'
						className='color-tertiary-400'
					>
						Backup Code Exhausted
					</Typography>
					<Typography className='color-primary-100'>
						You&apos;ve used all available backup codes. Contact support to regain access to reset.
					</Typography>

					<Button onClick={openIntercom(intercomMsg)}>Contact Support</Button>
				</div>
			</ModalParts.Stage>
		</ModalParts.Wrapper>
	);
};

export default DialogVerifyTransaction;
